import axios from "axios";
import { EMAIL_REGEXP, ERROR_MESSAGES, STORAGE_KEYS } from "../../utils/constants";
import { DEFAULT_OPERATOR } from "../../utils/operators";
import { generateOTPAPI, validateOTPAPI, signUpAPI } from "../constants/apis";
import { loginV2, loginV1, storeAuthTokens, clearAuthTokens } from "../constants/authApis";
import { getDeviceInfo } from "../../utils/deviceInfo";
import { sendLog } from "../utils/sendLog";
import {
  setIsLoading,
  setIsAuthenticated,
  setAuthError,
  setEmail as setEmailAction,
  setUsername as setUsernameAction,
} from "./slice/authSlice";
import store from "./store";
import { fetchProfile } from "./account";
import { getCrmApiUrl, getCrmAuthHeaders } from "../config/apiConfig";

/**
 * Auth Business Logic — MangoTV
 * Passwordless OTP-only authentication.
 *
 * Flow overview:
 *   Get Started:  validate email → generateOTP → /otp → verifyOTP → signUp (if new) → login → /home
 *
 * NOTE: The Client API still requires a password field. A default password is
 *       used internally so the user never needs to enter one.
 */

// Default password used for all accounts (backend still requires a password field)
const DEFAULT_PASSWORD = "1234567";

// ==============================================================================
// HELPERS
// ==============================================================================

const getOperator = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_OPERATOR);
    if (stored) return JSON.parse(stored);
  } catch {
    // ignore
  }
  return DEFAULT_OPERATOR;
};

/**
 * Format login username for the Client API: email@operator_uid
 */
const formattedLogin = (email) => {
  const operator = getOperator();
  return `${email}@${operator.operator_uid}`;
};

const _verifyEmail = (email) => EMAIL_REGEXP.test(email);

// ==============================================================================
// OPERATOR INITIALIZATION
// ==============================================================================

/**
 * Initialize the operator in localStorage (called on app boot).
 * MangoTV always uses 9mobile operator.
 */
export const initializeOperator = () => {
  const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_OPERATOR);
  if (!stored) {
    localStorage.setItem(
      STORAGE_KEYS.SELECTED_OPERATOR,
      JSON.stringify(DEFAULT_OPERATOR)
    );
  }
};

// ==============================================================================
// OTP
// ==============================================================================

/**
 * Generate OTP — sends OTP to email
 */
export const generateOTP = async (email) => {
  try {
    await axios.post(generateOTPAPI(), { email: email });
  } catch (error) {
    console.error("Generate OTP error:", error.message);
    store.dispatch(setAuthError(ERROR_MESSAGES.GENERIC_ERROR));
  }
};

/**
 * Validate OTP — verifies the 6-digit code
 */
const validateOTP = async (email, otpCode) => {
  if (otpCode.length < 6) {
    store.dispatch(setAuthError(ERROR_MESSAGES.AUTH.wrongOTP));
    return null;
  }

  const response = await axios.post(validateOTPAPI(), {
    email: email,
    otp: otpCode,
  }, {
    headers: {
      Password: "1234567",
      Username: "tva",
    },
  });
  return response.data;
};

// ==============================================================================
// UNIFIED AUTH FLOW (passwordless)
// ==============================================================================

/**
 * Step 1: Validate email → Generate OTP → navigate to OTP screen
 * Replaces both initiateSignIn and initiateSignUp.
 */
export const initiateAuth = async (email, navigate) => {
  store.dispatch(setIsLoading(true));
  store.dispatch(setAuthError(null));

  if (!_verifyEmail(email)) {
    store.dispatch(setAuthError(ERROR_MESSAGES.AUTH.invalidEmail));
    store.dispatch(setIsLoading(false));
    return;
  }

  // Invalidate any prior session before starting a new auth flow. Without
  // this, stale tokens from a previous login would let the user bypass OTP
  // by typing a protected route in the URL bar after being redirected to
  // /otp-verification — ProtectedRoute only checks for mango_access_token.
  clearAuthTokens();
  store.dispatch(setIsAuthenticated(false));

  // Store credentials
  localStorage.setItem(STORAGE_KEYS.EMAIL, email);
  localStorage.setItem(STORAGE_KEYS.USERNAME, email);
  store.dispatch(setEmailAction(email));
  store.dispatch(setUsernameAction(email));

  try {
    await generateOTP(email);
    store.dispatch(setIsLoading(false));
    navigate("/otp-verification");
  } catch (error) {
    store.dispatch(setIsLoading(false));
    store.dispatch(setAuthError(ERROR_MESSAGES.GENERIC_ERROR));
  }
};

/**
 * Step 2: Verify OTP → try sign-up (creates account if new) → login → /home
 * If the subscriber already exists, it skips sign-up and goes straight to login.
 */
export const verifyAuthOTP = async (otpCode) => {
  store.dispatch(setIsLoading(true));
  store.dispatch(setAuthError(null));
  const email = localStorage.getItem(STORAGE_KEYS.EMAIL);

  try {
    const otpResult = await validateOTP(email, otpCode);

    if (!otpResult || otpResult.status === "error") {
      store.dispatch(setIsLoading(false));
      store.dispatch(setAuthError(otpResult?.data?.message || ERROR_MESSAGES.AUTH.wrongOTP));
      return false;
    }

    if (otpResult.status === "ok") {
      // Try to sign up — if subscriber exists, just login
      const signupPayload = {
        first_name: "Mango",
        last_name: "TV",
        email: email,
        password: DEFAULT_PASSWORD,
        username: email,
      };

      try {
        const signupRes = await axios.post(signUpAPI(), signupPayload);

        if (
          signupRes.data.status === "ok" ||
          signupRes.data.message === "subscriber already exist"
        ) {
          return await performLogin(email, DEFAULT_PASSWORD);
        }

        if (signupRes.data.status === "error") {
          store.dispatch(setIsLoading(false));
          store.dispatch(setAuthError(`Oops! ${signupRes.data.message}. Try again`));
          return false;
        }
      } catch {
        // Sign-up request failed — subscriber may already exist, try login directly
        return await performLogin(email, DEFAULT_PASSWORD);
      }
    }
  } catch (error) {
    store.dispatch(setIsLoading(false));
    store.dispatch(setAuthError(ERROR_MESSAGES.GENERIC_ERROR));
    return false;
  }
};

// ==============================================================================
// LOGIN (Client API — V2 with V1 fallback)
// ==============================================================================

/**
 * Perform the actual login via Client API
 * Username format: email@operator_uid (e.g. user@example.com@9mobile)
 * Tries V2 first, falls back to V1 if V2 fails.
 */
/**
 * PUT the shared default password onto an existing CRM subscriber, so accounts
 * created before passwordless sign-in (with a user-chosen password) can log in
 * with DEFAULT_PASSWORD. Only called after the OTP has proved the user owns the
 * email. Never blocks login: a failure is logged and login goes ahead.
 */
const resetToDefaultPassword = async (email) => {
  const operator = getOperator();
  try {
    const res = await axios.put(
      `${getCrmApiUrl()}/subscriber/?operator_uid=${operator.operator_uid}&subscriber_uid=${encodeURIComponent(email)}`,
      { password: DEFAULT_PASSWORD },
      { headers: { ...getCrmAuthHeaders(), "Content-Type": "application/json" } }
    );
    if (res.data?.message !== "subscriber account updated" && res.data?.status !== "ok") {
      console.warn("[auth] default-password reset not applied:", res.data);
    }
  } catch (e) {
    console.warn("[auth] default-password reset failed:", e.response?.data || e.message);
  }
};

const performLogin = async (email, password) => {
  const formattedUser = formattedLogin(email);
  const deviceInfo = getDeviceInfo();

  // Reset older accounts to the default password before logging in with it.
  if (password === DEFAULT_PASSWORD) {
    await resetToDefaultPassword(email);
  }

  try {
    let loginResponse;
    try {
      // V2 login (recommended)
      loginResponse = await loginV2({
        login_type: "Credentials",
        username: formattedUser,
        password: password,
        device: deviceInfo.device,
        device_class: deviceInfo.device_class,
        device_type: deviceInfo.device_type,
        device_os: deviceInfo.device_os,
        device_mac_1: deviceInfo.device_mac_1,
        device_metadata: deviceInfo.device_metadata,
      });
    } catch {
      // Fallback to V1
      loginResponse = await loginV1({
        username: formattedUser,
        password: password,
        device_class: deviceInfo.device_class,
        device_type: deviceInfo.device_type,
        device_os: deviceInfo.device_os,
        device_mac_1: deviceInfo.device_mac_1,
        device_metadata: deviceInfo.device_metadata,
      });
    }

    if (loginResponse.status === "ok") {
      storeAuthTokens(loginResponse.data);
      store.dispatch(setIsAuthenticated(true));
      store.dispatch(setIsLoading(false));
      // Log login event to CRM (fire-and-forget)
      sendLog({ action: "login" });
      // Fetch profile in background after successful login
      fetchProfile().catch(() => {});
      return true;
    }
  } catch (error) {
    store.dispatch(setIsLoading(false));
    store.dispatch(setAuthError(ERROR_MESSAGES.AUTH.invalidLogin));
    return false;
  }
};

// ==============================================================================
// LOGOUT
// ==============================================================================

export const logoutUser = async () => {
  // Log logout event to CRM BEFORE clearing tokens (so subscriber_uid is still available)
  try {
    await sendLog({ action: "logout" });
  } catch {
    // never block logout on log failure
  }

  try {
    const { logout } = await import("../constants/authApis");
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (accessToken) {
      await logout(accessToken);
    }
  } catch {
    // server-side logout failed, still clear local tokens
  }

  clearAuthTokens();
  store.dispatch(setIsAuthenticated(false));
  window.location.href = "/";
};
